/**
 * Clients section — full-page список клиентов + карточка клиента.
 *
 * Phase 3 of CRM transformation (2026-05-08).
 *
 * Возможности:
 *   - Список клиентов (карточки): имя, email, phone, кол-во смет, total_estimated
 *   - Live-поиск (по name / email / phone, debounced)
 *   - Сортировка: name / last_estimate_at / estimates_count / total_estimated
 *   - Click на карточку → детали клиента + список смет
 *   - Кнопка «+ Новый клиент» — модалка
 *   - В детальной карточке: контакты + история смет с click → калькулятор
 *
 * @module crm-shell/sections/clients
 */

import { fetchAuth } from '../api.js';  // Sprint 20w: auto-refresh on 401

// i18n helper: returns translation if loaded, else fallback (RU). Safe to call before module loads.
const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

const STATE = {
    list: [],
    filtered: [],
    search: '',
    sort: 'last_estimate_at',
    sortDir: 'desc',
    currentClient: null,
    currentEstimates: [],
    view: 'list', // 'list' | 'card'
    // Sprint 18: row-based UI with expandable estimates
    expandedClientId: null,                // id раскрытой строки
    inlineEstimatesCache: Object.create(null), // {[clientId]: estimates[]}
    // Sprint 18 Batch 3: assignment filter
    assignedFilter: 'all',                 // 'all' | 'me' | 'unassigned' | <user_id>
    managers: [],                          // cache от GET /users/managers
    // Sprint 23 W6: source/quality/tag filters + dedup
    sourceFilter: 'all',                   // 'all' | 'tripster' | 'manual' | ...
    statusFilter: 'all',                   // 'all' | 'lead' | 'active' | 'lost' | ...
    hasContactFilter: '',                  // '' (all) | '1' (only with) | '0' (only without)
    archivedFilter: false,                 // bool — show lost/archived too
    tagsFilter: [],                        // ['vip', 'tripster', ...]
    tagCloud: [],                          // [{ tag, count }]
    sourceCounts: []                       // [{ source, count }]
};

// Sprint 23 W6: source visual metadata for badges
const SOURCE_META = {
    tripster:    { icon: '🌐', label: 'Tripster',   color: '#0891b2' },
    instagram:   { icon: '📷', label: 'Instagram',  color: '#db2777' },
    google_ads:  { icon: '🔎', label: 'Google',     color: '#2563eb' },
    referral:    { icon: '🤝', label: 'Реферал',    color: '#16a34a' },
    agency:      { icon: '🏢', label: 'Агентство',  color: '#7c3aed' },
    repeat:      { icon: '🔁', label: 'Возврат',    color: '#0d9488' },
    unknown:     { icon: '❓', label: 'Неизвестно', color: '#6b7280' },
    manual:      { icon: '✍️', label: 'Вручную',    color: '#475569' }
};

function parseTags(raw) {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw); } catch { return []; }
}

function completenessLevel(c) {
    let n = 0;
    if (c.email) n++;
    if (c.phone) n++;
    if (c.notes && String(c.notes).length > 10) n++;
    if ((c.estimates_count || 0) > 0) n++;
    return n >= 3 ? 'high' : (n >= 1 ? 'med' : 'low');
}

let _container = null;

// ─── Helpers ────────────────────────────────────────────────────────────────
function fmtDate(ts) {
    // Sprint 24: timezone-aware via __dtFormat__
    if (!ts) return '—';
    let val = ts;
    if (typeof val === 'string') {
        const ms = Date.parse(val);
        if (isNaN(ms)) return '—';
        val = Math.floor(ms / 1000);
    }
    if (window.__dtFormat__?.formatDate) return window.__dtFormat__.formatDate(val);
    const d = typeof val === 'number' ? new Date(val < 1e12 ? val * 1000 : val) : new Date(val);
    if (isNaN(d.getTime())) return '—';
    const loc = (window.i18n?.getLocale?.() === 'en' ? 'en-GB' : (window.i18n?.getLocale?.() === 'es' ? 'es-AR' : 'ru-RU'));
    return d.toLocaleDateString(loc, { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtCurrency(n) {
    if (n == null) return '$0';
    return '$' + Math.round(Number(n) || 0).toLocaleString('en-US');
}

function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

// ─── Data loading ───────────────────────────────────────────────────────────
async function loadList() {
    if (!window.apiClient) return { error: _t('clients.error.api_not_ready', 'apiClient не готов') };
    try {
        const params = new URLSearchParams({
            limit: '500',
            sort: STATE.sort,
            dir: STATE.sortDir
        });
        if (STATE.search) params.set('q', STATE.search);
        if (STATE.assignedFilter && STATE.assignedFilter !== 'all') {
            params.set('assigned_to', STATE.assignedFilter);
        }
        // Sprint 23 W6: source/tags/quality filters
        if (STATE.sourceFilter && STATE.sourceFilter !== 'all') {
            params.set('source', STATE.sourceFilter);
        }
        if (STATE.statusFilter && STATE.statusFilter !== 'all') {
            params.set('status', STATE.statusFilter);
        }
        if (STATE.hasContactFilter) {
            params.set('has_contact', STATE.hasContactFilter);
        }
        if (STATE.archivedFilter) {
            params.set('archived', '1');
        }
        if (STATE.tagsFilter.length > 0) {
            params.set('tags', STATE.tagsFilter.join(','));
        }
        const r = await fetchAuth('/api/v1/clients?' + params.toString());
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const data = (await r.json()).data || {};
        STATE.list = data.clients || [];
        STATE.filtered = STATE.list;
        return { ok: true };
    } catch (err) {
        return { error: err.message };
    }
}

// Sprint 23 W6: tag cloud + source counts (refreshed alongside list)
async function loadTagCloud() {
    try {
        const r = await fetchAuth('/api/v1/clients/meta/tag-cloud');
        if (!r.ok) return;
        const d = await r.json();
        STATE.tagCloud = d.data || [];
    } catch (err) {
        console.warn('[clients] loadTagCloud failed:', err);
    }
}

async function loadSourceCounts() {
    try {
        const r = await fetchAuth('/api/v1/clients/meta/source-counts');
        if (!r.ok) return;
        const d = await r.json();
        STATE.sourceCounts = d.data || [];
    } catch (err) {
        console.warn('[clients] loadSourceCounts failed:', err);
    }
}

// Sprint 18 Batch 3: lazy load managers list для assignment dropdown
async function loadManagers() {
    if (STATE.managers.length > 0) return STATE.managers;
    try {
        const r = await fetchAuth('/api/v1/users/managers');
        if (!r.ok) return [];
        const d = await r.json();
        STATE.managers = d.data?.managers || [];
        return STATE.managers;
    } catch (err) {
        console.warn('[clients] loadManagers failed (non-fatal):', err);
        return [];
    }
}

async function loadCard(id) {
    if (!window.apiClient) return { error: _t('clients.error.api_not_ready', 'apiClient не готов') };
    try {
        const data = await window.apiClient.getClient(id);
        STATE.currentClient = data.client;
        STATE.currentEstimates = data.estimates || [];
        return { ok: true };
    } catch (err) {
        return { error: err.message };
    }
}

// ─── Renderers ──────────────────────────────────────────────────────────────
function renderToolbar() {
    const STATUS_LABELS = {
        lead: 'Лид', negotiating: 'Переговоры', active: 'Активный',
        completed: 'Завершён', lost: 'Потерян', archived: 'Архив'
    };

    const sourceOpts = ['all', 'tripster', 'manual', 'referral', 'agency', 'instagram', 'google_ads', 'repeat', 'unknown']
        .map(k => {
            const cnt = STATE.sourceCounts.find(s => s.source === k);
            const m = SOURCE_META[k];
            const label = k === 'all' ? 'Все источники' : `${m ? m.icon + ' ' + m.label : k}${cnt ? ` (${cnt.count})` : ''}`;
            return `<option value="${k}"${STATE.sourceFilter === k ? ' selected' : ''}>${label}</option>`;
        }).join('');

    const activeTagsChips = STATE.tagsFilter.length > 0
        ? `<div class="cl-active-tags" style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;padding:6px 0;font-size:12px;">
            <span style="color:var(--text-muted);">Теги:</span>
            ${STATE.tagsFilter.map(t => `<span class="cl-active-tag" data-tag-remove="${escapeHtml(t)}"
                style="background:var(--primary-600,#2563eb);color:#fff;padding:3px 10px;border-radius:12px;cursor:pointer;display:inline-flex;gap:4px;align-items:center;"
                title="Кликни чтобы убрать">${escapeHtml(t)} <span style="opacity:.7;">✕</span></span>`).join('')}
            <button id="cl-clear-tags" class="btn-icon" style="font-size:11px;padding:3px 8px;">Очистить</button>
           </div>` : '';

    const tagCloud = STATE.tagCloud.length > 0
        ? `<div class="cl-tag-cloud" style="display:flex;gap:5px;flex-wrap:wrap;padding:6px 0;font-size:12px;align-items:center;">
            <span style="color:var(--text-muted);margin-right:4px;">Теги:</span>
            ${STATE.tagCloud.slice(0, 20).map(t => {
                const active = STATE.tagsFilter.includes(t.tag);
                return `<span class="cl-tag-chip" data-tag="${escapeHtml(t.tag)}"
                    style="padding:3px 9px;border-radius:11px;cursor:pointer;background:${active ? 'var(--primary-600,#2563eb)' : '#eff6ff'};color:${active ? '#fff' : '#1e40af'};font-size:11px;transition:.15s;">
                    ${escapeHtml(t.tag)}<span style="opacity:.65;margin-left:4px;">${t.count}</span></span>`;
            }).join('')}
           </div>` : '';

    return `
        <div class="estimates-toolbar">
            <div class="clients-subnav">
                <a class="clients-subnav-tab active" href="#/clients" data-subnav="by-client"><i data-lucide="users" style="width:14px;height:14px;display:inline-block;vertical-align:-2px;margin-right:4px"></i>${_t('clients.subnav.by_client', 'По клиентам')}</a>
                <a class="clients-subnav-tab" href="#/estimates" data-subnav="all-estimates"><i data-lucide="clipboard-list" style="width:14px;height:14px;display:inline-block;vertical-align:-2px;margin-right:4px"></i>${_t('clients.subnav.all_estimates', 'Все сметы')}</a>
            </div>
            <div class="estimates-toolbar-row">
                <button id="clients-new-btn" class="btn-primary">${_t('clients.btn.new', '+ Новый клиент')}</button>
                <div class="estimates-search-wrap">
                    <input id="clients-search" type="text"
                           placeholder="${escapeHtml(_t('clients.search_placeholder', "Поиск (поддерживает кириллицу↔латиницу: 'ivan' найдёт 'Иван')"))}"
                           value="${escapeHtml(STATE.search)}" />
                </div>
                <!-- Sprint 23 W6: source filter -->
                <select id="clients-source-filter" class="estimates-select" title="Фильтр по источнику">
                    ${sourceOpts}
                </select>
                <!-- Sprint 23 W6: status filter -->
                <select id="clients-status-filter" class="estimates-select" title="Статус клиента">
                    <option value="all"${STATE.statusFilter === 'all' ? ' selected' : ''}>Все статусы</option>
                    ${Object.entries(STATUS_LABELS).map(([k, l]) =>
                        `<option value="${k}"${STATE.statusFilter === k ? ' selected' : ''}>${l}</option>`
                    ).join('')}
                </select>
                <!-- Sprint 23 W6: has_contact filter -->
                <select id="clients-contact-filter" class="estimates-select" title="С контактами / без">
                    <option value=""${STATE.hasContactFilter === '' ? ' selected' : ''}>С контактами и без</option>
                    <option value="1"${STATE.hasContactFilter === '1' ? ' selected' : ''}>✓ Только с контактами</option>
                    <option value="0"${STATE.hasContactFilter === '0' ? ' selected' : ''}>✗ Без контактов</option>
                </select>
                <!-- Sprint 18 Batch 3: assignment filter -->
                <select id="clients-assigned-filter" class="estimates-select" title="${escapeHtml(_t('clients.filter.title', 'Фильтр по менеджеру'))}">
                    <option value="all" ${STATE.assignedFilter === 'all' ? 'selected' : ''}>${_t('clients.filter.all', 'Все клиенты')}</option>
                    <option value="me" ${STATE.assignedFilter === 'me' ? 'selected' : ''}>${_t('clients.filter.mine', 'Мои клиенты (★)')}</option>
                    <option value="unassigned" ${STATE.assignedFilter === 'unassigned' ? 'selected' : ''}>${_t('clients.filter.unassigned', 'Без менеджера')}</option>
                </select>
                <select id="clients-sort" class="estimates-select">
                    <option value="last_estimate_at" ${STATE.sort === 'last_estimate_at' ? 'selected' : ''}>${_t('clients.sort.last_estimate', 'По последней смете')}</option>
                    <option value="name" ${STATE.sort === 'name' ? 'selected' : ''}>${_t('clients.sort.name', 'По имени')}</option>
                    <option value="estimates_count" ${STATE.sort === 'estimates_count' ? 'selected' : ''}>${_t('clients.sort.count', 'По кол-ву смет')}</option>
                    <option value="total_estimated" ${STATE.sort === 'total_estimated' ? 'selected' : ''}>${_t('clients.sort.total', 'По сумме')}</option>
                </select>
                <button id="clients-sort-dir" class="btn-icon">
                    ${STATE.sortDir === 'asc' ? '↑' : '↓'}
                </button>
                <!-- Sprint 23 W6: archived toggle + duplicates -->
                <label style="display:flex;align-items:center;gap:4px;font-size:12px;color:var(--text-muted);cursor:pointer;margin:0 6px;">
                    <input type="checkbox" id="clients-archived-toggle" ${STATE.archivedFilter ? 'checked' : ''} style="margin:0;" />
                    ${_t('clients.filter.archived', 'Архив')}
                </label>
                <button id="clients-duplicates-btn" class="btn-icon" title="${escapeHtml(_t('clients.duplicates.title', 'Найти возможные дубликаты по email/телефону'))}">🔍 ${_t('clients.duplicates.btn', 'Дубли')}</button>
                <span class="estimates-count">${_t('clients.count', '{n} клиентов').replace('{n}', STATE.filtered.length)}</span>
            </div>
            ${activeTagsChips}
            ${tagCloud}
        </div>
    `;
}

// Sprint 18 v2: упрощённый row — primary action = OPEN CARD.
// Chevron вынесен в отдельный clickable element (только expand).
// Name/avatar открывают карточку клиента.
function renderClientRow(c) {
    const isExpanded = STATE.expandedClientId === c.id;
    const inlineEstimates = STATE.inlineEstimatesCache[c.id];
    const initials = (c.name || '?').split(/\s+/).map(w => w[0]).filter(Boolean).slice(0,2).join('').toUpperCase();
    const contactBits = [
        c.email ? `<span class="cr-contact"><i data-lucide="mail" class="cr-icon" style="width:11px;height:11px;display:inline-block;vertical-align:-1px;margin-right:3px"></i>${escapeHtml(c.email)}</span>` : '',
        c.phone ? `<span class="cr-contact"><i data-lucide="phone" class="cr-icon" style="width:11px;height:11px;display:inline-block;vertical-align:-1px;margin-right:3px"></i>${escapeHtml(c.phone)}</span>` : '',
        c.telegram ? `<span class="cr-contact"><i data-lucide="send" class="cr-icon" style="width:11px;height:11px;display:inline-block;vertical-align:-1px;margin-right:3px"></i>@${escapeHtml(c.telegram)}</span>` : ''
    ].filter(Boolean).join('') || `<span class="cr-no-contact">${_t('clients.row.no_contacts', '— нет контактов —')}</span>`;

    const estCount = c.estimates_count || 0;
    // RU has plural form (1 смета / 2..4 сметы / 5+ смет). EN/ES use singular/plural only.
    const loc = window.i18n?.getLocale?.() || 'ru';
    let estCountLabel;
    if (estCount === 0) {
        estCountLabel = _t('clients.row.no_estimates', 'нет смет');
    } else if (estCount === 1) {
        estCountLabel = _t('clients.row.one_estimate', '1 смета');
    } else if (loc === 'ru' && estCount % 10 >= 2 && estCount % 10 <= 4 && Math.floor(estCount / 10) % 10 !== 1) {
        estCountLabel = _t('clients.row.few_estimates', '{n} сметы').replace('{n}', estCount);
    } else {
        estCountLabel = _t('clients.row.many_estimates', '{n} смет').replace('{n}', estCount);
    }

    // Sprint 23 W6: source badge + completeness dot + tag mini-chips
    const src = c.source || 'unknown';
    const sm = SOURCE_META[src] || SOURCE_META.unknown;
    const level = completenessLevel(c);
    const dotColor = level === 'high' ? '#16a34a' : (level === 'med' ? '#f59e0b' : '#dc2626');
    const dotTitle = level === 'high' ? 'Полные данные' : (level === 'med' ? 'Частичные данные' : 'Нет контактов');
    const tags = parseTags(c.tags);
    const tagBadges = tags.length > 0
        ? `<span class="cr-tags" style="display:inline-flex;gap:3px;flex-wrap:wrap;margin-top:2px;">
            ${tags.slice(0, 3).map(t => `<span style="font-size:10px;padding:1px 6px;background:#eff6ff;color:#1e40af;border-radius:8px;">${escapeHtml(t)}</span>`).join('')}
            ${tags.length > 3 ? `<span style="font-size:10px;padding:1px 6px;background:#f3f4f6;color:#6b7280;border-radius:8px;">+${tags.length - 3}</span>` : ''}
           </span>` : '';
    const sourceBadge = `<span class="cr-source-badge" title="Источник: ${sm.label}"
        style="display:inline-flex;align-items:center;gap:3px;padding:2px 7px;border-radius:9px;font-size:10px;font-weight:500;color:#fff;background:${sm.color};margin-left:6px;">${sm.icon} ${sm.label}</span>`;
    const dotMarker = `<span title="${dotTitle}" style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${dotColor};margin-right:5px;vertical-align:middle;"></span>`;

    return `
        <div class="client-row ${isExpanded ? 'expanded' : ''}" data-client-id="${c.id}">
            <div class="cr-toggle" data-action="toggle" title="${escapeHtml(isExpanded ? _t('clients.row.collapse', 'Свернуть') : _t('clients.row.show_estimates', 'Показать сметы'))}">
                <span class="cr-chevron">${isExpanded ? '▾' : '▸'}</span>
            </div>
            <div class="cr-main" data-action="toggle" title="${escapeHtml(_t('clients.row.click_to_expand', 'Кликни чтобы развернуть сметы клиента'))}">
                <div class="cr-avatar">${escapeHtml(initials)}</div>
                <div class="cr-info">
                    <div class="cr-name">${dotMarker}${escapeHtml(c.name)}${sourceBadge}</div>
                    <div class="cr-contacts">${contactBits}</div>
                    ${tagBadges}
                </div>
            </div>
            <div class="cr-stats" data-action="toggle" title="${escapeHtml(_t('clients.row.click_to_view', 'Кликни чтобы посмотреть сметы'))}">
                <div class="cr-stat-est">${estCountLabel}</div>
                ${c.last_estimate_at ? `<div class="cr-stat-last">${_t('clients.row.last_updated', 'обн. {date}').replace('{date}', fmtDate(c.last_estimate_at))}</div>` : ''}
            </div>
            <div class="cr-total" title="${escapeHtml(_t('clients.row.total_amount_title', 'Общая сумма по сметам'))}">${fmtCurrency(c.total_estimated)}</div>
            <div class="cr-actions">
                <button class="btn-action btn-action-primary" data-action="new-estimate" title="${escapeHtml(_t('clients.row.btn.new_estimate_title', 'Создать смету для клиента'))}">${_t('clients.row.btn.new_estimate', '+ Смета')}</button>
                <button class="btn-action" data-action="open-card" title="${escapeHtml(_t('clients.row.btn.open_card_title', 'Открыть полную карточку клиента'))}">${_t('clients.row.btn.open_card', 'Карточка ↗')}</button>
                <button class="btn-action btn-action-danger" data-action="delete" title="${_t('btn.delete', 'Удалить')}">×</button>
            </div>
            ${isExpanded ? `<div class="client-row-expanded">${renderInlineEstimates(c, inlineEstimates)}</div>` : ''}
        </div>
    `;
}

/**
 * Sprint 18: shared estimates table renderer (DRY).
 * Используется в:
 *   - inline expanded rows клиента (clients.js)
 *   - карточка клиента (renderCardView)
 *   - (planned) /#/estimates view
 *
 * @param {Array} estimates - sorted array of estimates
 * @param {Object} opts - { showDelete:bool, emptyMessage:string, clientId:string }
 * @returns {string} HTML
 */
function renderEstimatesTable(estimates, opts = {}) {
    const { showDelete = true, emptyMessage = _t('clients.table.no_estimates_for_client', 'У клиента ещё нет смет.'), clientId = null } = opts;
    if (!estimates) {
        return `<div class="estimates-loading" style="padding:14px 20px;">${_t('common.loading', 'Загрузка...')}</div>`;
    }
    if (estimates.length === 0) {
        const newLink = clientId
            ? ` <a href="javascript:void(0)" data-action="new-estimate-link" data-client-id="${clientId}" style="color:var(--primary-600);">${_t('clients.table.create_first', 'Создать первую')}</a>`
            : '';
        return `<div style="padding:14px 20px;color: var(--text-muted);">${emptyMessage}${newLink}</div>`;
    }
    const STATUS_CFG = {
        draft:       { label: _t('estimate.status.draft', 'Черновик'),         bg: '#f1f5f9', text: '#475569' },
        sent:        { label: _t('estimate.status.sent', 'Отправлено'),        bg: '#dbeafe', text: '#1d4ed8' },
        confirmed:   { label: _t('estimate.status.confirmed', 'Подтверждено'), bg: '#fef3c7', text: '#92400e' },
        prepaid:     { label: _t('estimate.status.prepaid', 'Предоплачено'),   bg: '#fce7f3', text: '#9d174d' },
        paid:        { label: _t('estimate.status.paid', 'Оплачено'),          bg: '#d1fae5', text: '#065f46' },
        in_progress: { label: _t('estimate.status.in_progress', 'В работе'),   bg: '#e0e7ff', text: '#3730a3' },
        completed:   { label: _t('estimate.status.completed', 'Завершено'),    bg: '#dcfce7', text: '#166534' },
        closed:      { label: _t('estimate.status.closed', 'Закрыто'),         bg: '#f3f4f6', text: '#374151' },
        cancelled:   { label: _t('estimate.status.cancelled', 'Отменено'),     bg: '#fee2e2', text: '#991b1b' }
    };
    const statusBadge = (s) => {
        const c = STATUS_CFG[s || 'draft'] || STATUS_CFG.draft;
        return `<span class="estimate-lifecycle-badge" style="background:${c.bg};color:${c.text}">${c.label}</span>`;
    };

    // Sprint 19e: компактный pipeline-индикатор (8 точек) под названием сметы
    const PIPELINE_ORDER = ['draft','sent','confirmed','prepaid','in_progress','paid','completed','closed'];
    const renderPipelineMini = (e) => {
        const status = e.status || 'draft';
        if (status === 'cancelled') {
            return `<div style="font-size:10px;color:#991b1b;margin-top:2px;">${_t('clients.table.cancelled_indicator', '⊗ отменена')}</div>`;
        }
        const idx = PIPELINE_ORDER.indexOf(status);
        const dots = PIPELINE_ORDER.map((s, i) => {
            const cfg = STATUS_CFG[s];
            const isDone = i < idx;
            const isCurrent = i === idx;
            const color = isCurrent ? cfg.text : (isDone ? '#2563eb' : '#e2e8f0');
            const size = isCurrent ? 7 : 5;
            return `<span title="${cfg.label}" style="display:inline-block;width:${size}px;height:${size}px;border-radius:50%;background:${color};margin:0 1px;vertical-align:middle"></span>`;
        }).join('');
        // Phase track flags (если уже взведены)
        const flags = [];
        if (e.bookings_complete_at) flags.push(_t('clients.table.flag.booking', 'бронь ✓'));
        if (e.suppliers_paid_at) flags.push(_t('clients.table.flag.suppliers_paid', 'пост.опл ✓'));
        if (e.tour_started_at) flags.push(_t('clients.table.flag.tour_started', 'старт ✓'));
        if (e.tour_ended_at) flags.push(_t('clients.table.flag.tour_ended', 'конец ✓'));
        const flagsHtml = flags.length ? `<span style="margin-left:6px;color:#16a34a;">${flags.join(' · ')}</span>` : '';
        return `<div style="font-size:10px;color: var(--text-muted);margin-top:3px;">${dots}${flagsHtml}</div>`;
    };

    return `
        <table class="client-estimates-table">
            <thead>
                <tr>
                    <th>${_t('clients.table.col.tour_name', 'Название тура / Файл')}</th>
                    <th style="width:90px;">${_t('clients.table.col.status', 'Статус')}</th>
                    <th style="width:80px;">${_t('clients.table.col.pax', 'Pax')}</th>
                    <th style="width:160px;">${_t('clients.table.col.tour_dates', 'Даты тура')}</th>
                    <th style="width:120px;text-align:right;">${_t('clients.table.col.amount', 'Сумма')}</th>
                    <th style="width:120px;">${_t('clients.table.col.updated', 'Обновлена')}</th>
                    <th style="width:${showDelete ? '120' : '80'}px;text-align:right;">${_t('clients.table.col.actions', 'Действия')}</th>
                </tr>
            </thead>
            <tbody>
                ${estimates.map(e => {
                    // Sprint 19e: friendly название — клиент + тур + статус
                    const tourPart = (e.tour_name || '').trim();
                    const clientPart = (e.client_name || '').trim();
                    let friendlyName;
                    if (tourPart && clientPart) {
                        friendlyName = `${tourPart} · ${clientPart}`;
                    } else if (tourPart) {
                        friendlyName = tourPart;
                    } else if (clientPart) {
                        friendlyName = _t('clients.table.estimate_for', 'Смета {name}').replace('{name}', clientPart);
                    } else {
                        friendlyName = _t('clients.table.unnamed', '(без названия)');
                    }
                    const dateRange = e.tour_start
                        ? fmtDate(e.tour_start) + (e.tour_end ? ' → ' + fmtDate(e.tour_end) : '')
                        : '—';
                    return `
                    <tr data-estimate-id="${e.id}" data-action="open-estimate" style="cursor:pointer;">
                        <td>
                            <div style="font-weight:500;">${escapeHtml(friendlyName)}</div>
                            ${renderPipelineMini(e)}
                            ${e.is_archived_variant ? `<span style="font-size:10px;color: var(--text-muted);background: var(--surface-elevated);padding:1px 6px;border-radius:3px;">${_t('clients.table.archived_variant', 'архивный вариант')}</span>` : ''}
                        </td>
                        <td>${statusBadge(e.status)}</td>
                        <td>${e.pax_count || 0}</td>
                        <td>${dateRange}</td>
                        <td style="text-align:right;font-variant-numeric:tabular-nums;">${fmtCurrency(e.total_cost)}</td>
                        <td style="color: var(--text-muted);">${fmtDate(e.updated_at)}</td>
                        <td style="text-align:right;white-space:nowrap;">
                            <!-- Sprint 22 W4: одна smart-кнопка "Карточка тура" — открывает интерактивный pipeline.
                                 Если у estimate нет tour_id (legacy / migration didn't fire) — fallback на calculator. -->
                            <button class="btn-icon" data-action="open-tour-card" data-tour-id="${e.tour_id || ''}" data-estimate-id="${e.id}"
                                    title="${escapeHtml(_t('clients.table.action.tour_card', 'Карточка тура (с pipeline)'))}">
                                <i data-lucide="map" style="width:13px;height:13px;vertical-align:-2px"></i>
                            </button>
                            <button class="btn-icon" data-action="open-estimate-btn" data-estimate-id="${e.id}"
                                    title="${escapeHtml(_t('clients.table.action.open_in_calc', 'Открыть в калькуляторе'))}" style="margin-left:4px;">
                                <i data-lucide="calculator" style="width:13px;height:13px;vertical-align:-2px"></i>
                            </button>
                            ${showDelete ? `<button class="btn-icon-warn" data-action="delete-estimate" data-estimate-id="${e.id}" title="${_t('btn.delete', 'Удалить')}" style="margin-left:4px;">×</button>` : ''}
                        </td>
                    </tr>
                `;}).join('')}
            </tbody>
        </table>
    `;
}

// Backward-compat wrapper для старого имени функции
function renderInlineEstimates(client, estimates) {
    return renderEstimatesTable(estimates, {
        showDelete: true,
        clientId: client?.id,
        emptyMessage: _t('clients.table.no_estimates_for_client', 'У клиента ещё нет смет.')
    });
}

function renderListView() {
    if (STATE.list.length === 0) {
        return `
            <div class="estimates-empty">
                <h3>${_t('clients.empty.title', 'Нет клиентов')}</h3>
                <p>${_t('clients.empty.hint', 'Создайте первого через кнопку «+ Новый клиент»<br>или они автоматически появятся при создании смет.')}</p>
            </div>
        `;
    }
    if (STATE.filtered.length === 0) {
        return `<div class="estimates-empty"><h3>${_t('clients.empty.not_found', 'Ничего не найдено')}</h3></div>`;
    }
    return `
        <div class="client-rows-table">
            ${STATE.filtered.map(renderClientRow).join('')}
        </div>
    `;
}

// Sprint 18: lazy-load estimates для раскрытой строки
async function loadInlineEstimates(clientId) {
    if (STATE.inlineEstimatesCache[clientId]) return; // уже кэшировано
    try {
        const r = await fetchAuth('/api/v1/estimates?client_id=' + encodeURIComponent(clientId) + '&limit=200');
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const d = await r.json();
        STATE.inlineEstimatesCache[clientId] = d.data?.estimates || [];
    } catch (err) {
        console.warn('[Clients] inline estimates load failed:', err);
        STATE.inlineEstimatesCache[clientId] = [];
    }
}

function renderCardView() {
    const c = STATE.currentClient;
    if (!c) return `<div class="estimates-loading">${_t('common.loading', 'Загрузка...')}</div>`;

    const tags = c.tags ? (typeof c.tags === 'string' ? JSON.parse(c.tags) : c.tags) : [];

    // Sprint 18: shared estimates table + per-row delete
    const estimatesHtml = renderEstimatesTable(STATE.currentEstimates, {
        showDelete: true,
        clientId: c.id,
        emptyMessage: _t('clients.table.no_estimates_yet', 'У этого клиента ещё нет смет.')
    });

    // Sprint 18 v2: status badge + business field widgets
    const STATUS_META = {
        lead:        { label: _t('clients.status.lead', '🌱 Лид'),               color: '#0369a1', bg: '#e0f2fe' },
        negotiating: { label: _t('clients.status.negotiating', '💬 Переговоры'),  color: '#92400e', bg: '#fef3c7' },
        active:      { label: _t('clients.status.active', '✅ Активный'),         color: '#15803d', bg: '#dcfce7' },
        completed:   { label: _t('clients.status.completed', '🎉 Завершён'),     color: '#5b21b6', bg: '#ede9fe' },
        lost:        { label: _t('clients.status.lost', '❌ Потерян'),            color: '#991b1b', bg: '#fee2e2' },
        archived:    { label: _t('clients.status.archived', '📦 Архив'),         color: '#475569', bg: '#f1f5f9' }
    };
    const stMeta = STATUS_META[c.status || 'lead'] || STATUS_META.lead;

    const followupHtml = c.next_followup_at ? (() => {
        const ts = c.next_followup_at;
        const diffDays = Math.ceil((ts * 1000 - Date.now()) / 86400000);
        const overdue = diffDays < 0;
        const soon = diffDays >= 0 && diffDays <= 3;
        const color = overdue ? '#991b1b' : (soon ? '#c2410c' : '#0369a1');
        const label = overdue
            ? _t('clients.card.followup.overdue', 'просрочен на {n} д').replace('{n}', Math.abs(diffDays))
            : (diffDays === 0
                ? _t('clients.card.followup.today', 'сегодня')
                : _t('clients.card.followup.in_days', 'через {n} д').replace('{n}', diffDays));
        return `<div style="font-size:12px;color:${color};">⏰ ${fmtDate(ts)} <span style="opacity:0.7;">(${label})</span></div>`;
    })() : `<div style="color: var(--text-muted);font-size:12px;">${_t('clients.card.followup.no_reminder', '— нет напоминания —')}</div>`;

    const SOURCE_LABELS = {
        unknown: _t('clients.source.unknown', '—'),
        instagram: _t('clients.source.instagram', '📷 Instagram'),
        google_ads: _t('clients.source.google_ads', '🔍 Google Ads'),
        referral: _t('clients.source.referral', '👥 Рекомендация'),
        agency: _t('clients.source.agency', '🏢 Агент'),
        repeat: _t('clients.source.repeat', '🔁 Повторный'),
        website: _t('clients.source.website', '🌐 Сайт')
    };

    return `
        <div class="client-card-view">
            <div class="client-card-header">
                <button id="clients-back-btn" class="btn-ghost">${_t('clients.card.back', '← К списку')}</button>
                <h2 style="flex:1; margin: 0 16px; font-size:20px;">
                    ${c.is_vip ? `<span title="${escapeHtml(_t('clients.card.vip_title', 'VIP клиент'))}" style="color:#eab308;">⭐</span> ` : ''}${escapeHtml(c.name)}
                    <span style="display:inline-block;margin-left:10px;padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600;color:${stMeta.color};background:${stMeta.bg};">${stMeta.label}</span>
                </h2>
                <button id="clients-edit-btn" class="btn-icon">✏️ ${_t('btn.edit', 'Редактировать')}</button>
            </div>

            <div class="client-info-grid">
                <!-- Контакты -->
                <div class="client-info-block">
                    <h4>${_t('clients.card.block.contacts', 'Контакты')}</h4>
                    <ul>
                        ${c.email ? `<li>✉️ <a href="mailto:${escapeHtml(c.email)}">${escapeHtml(c.email)}</a></li>` : ''}
                        ${c.phone ? `<li>📞 ${escapeHtml(c.phone)}${c.whatsapp ? ' <span style="color:#16a34a;">(WhatsApp)</span>' : ''}</li>` : ''}
                        ${c.telegram ? `<li>💬 <a href="https://t.me/${escapeHtml(c.telegram)}" target="_blank">@${escapeHtml(c.telegram)}</a></li>` : ''}
                        ${c.company ? `<li>🏢 ${escapeHtml(c.company)}</li>` : ''}
                        ${(c.country || c.city) ? `<li>📍 ${escapeHtml([c.city, c.country].filter(Boolean).join(', '))}</li>` : ''}
                        ${c.birthday ? `<li>🎂 ${escapeHtml(c.birthday)}</li>` : ''}
                        ${c.preferred_language && c.preferred_language !== 'ru' ? `<li>🌐 ${_t('clients.card.language', 'Язык')}: ${escapeHtml(c.preferred_language.toUpperCase())}</li>` : ''}
                        ${(!c.email && !c.phone && !c.telegram && !c.company) ? `<li style="color: var(--text-muted);">${_t('clients.card.no_contacts', '— нет контактов')}</li>` : ''}
                    </ul>
                </div>

                <!-- Статистика -->
                <div class="client-info-block">
                    <h4>${_t('clients.card.block.stats', 'Статистика')}</h4>
                    <ul>
                        <li>${_t('clients.card.stats.count', 'Смет')}: <strong>${c.estimates_count || 0}</strong></li>
                        <li>${_t('clients.card.stats.total', 'Сумма смет')}: <strong>${fmtCurrency(c.total_estimated)}</strong></li>
                        ${c.lifetime_value > 0 ? `<li>${_t('clients.card.stats.lifetime', '💰 Lifetime value (оплачено)')}: <strong style="color:#15803d;">${fmtCurrency(c.lifetime_value)}</strong></li>` : ''}
                        <li>${_t('clients.card.stats.first_seen', 'Первый контакт')}: <strong>${fmtDate(c.first_seen_at)}</strong></li>
                        <li>${_t('clients.card.stats.last_estimate', 'Последняя смета')}: <strong>${c.last_estimate_at ? fmtDate(c.last_estimate_at) : '—'}</strong></li>
                    </ul>
                </div>

                <!-- CRM Pipeline -->
                <div class="client-info-block">
                    <h4>${_t('clients.card.block.crm', 'CRM')}</h4>
                    <ul style="list-style:none;padding:0;">
                        <li style="margin-bottom:8px;">
                            <div style="font-size:11px;color: var(--text-muted);text-transform:uppercase;">${_t('clients.card.crm.manager', 'Менеджер')}</div>
                            ${c.assigned_full_name ? `
                                <div style="display:flex;align-items:center;gap:6px;">
                                    <span style="display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:50%;background:${c.assigned_avatar_color || '#6366f1'};color:white;font-size:11px;font-weight:600;">
                                        ${escapeHtml((c.assigned_full_name || c.assigned_email || '?').slice(0,1).toUpperCase())}
                                    </span>
                                    <span>${escapeHtml(c.assigned_full_name || c.assigned_email)}</span>
                                    <button id="reassign-btn" class="btn-icon" style="margin-left:auto;font-size:11px;" title="${escapeHtml(_t('clients.card.crm.reassign_title', 'Переназначить'))}">↻</button>
                                </div>
                            ` : `
                                <div style="display:flex;align-items:center;gap:6px;color: var(--text-muted);">
                                    <span>${_t('clients.card.crm.unassigned', '— не назначен —')}</span>
                                    <button id="reassign-btn" class="btn-icon" style="margin-left:auto;font-size:11px;color:var(--primary-600);" title="${escapeHtml(_t('clients.card.crm.assign_title', 'Назначить'))}">${_t('clients.card.crm.assign', '+ назначить')}</button>
                                </div>
                            `}
                        </li>
                        <li style="margin-bottom:8px;">
                            <div style="font-size:11px;color: var(--text-muted);text-transform:uppercase;">${_t('clients.card.crm.source', 'Источник')}</div>
                            <div>${SOURCE_LABELS[c.source] || c.source || '—'}</div>
                        </li>
                        <li style="margin-bottom:8px;">
                            <div style="font-size:11px;color: var(--text-muted);text-transform:uppercase;">${_t('clients.card.crm.next_contact', 'След. контакт')}</div>
                            ${followupHtml}
                        </li>
                        ${c.gdpr_consent_at ? `<li style="font-size:11px;color: var(--text-muted);">${_t('clients.card.crm.gdpr_consent', '✓ GDPR согласие от {date}').replace('{date}', fmtDate(c.gdpr_consent_at))}</li>` : ''}
                    </ul>
                </div>

                ${c.status === 'lost' && c.rejection_reason ? `
                    <div class="client-info-block" style="grid-column: 1 / -1;background:#fef2f2;border:1px solid #fca5a5;">
                        <h4 style="color:#991b1b;">${_t('clients.card.block.rejection_reason', '❌ Причина потери')}</h4>
                        <div style="white-space: pre-wrap;color:#7f1d1d;">${escapeHtml(c.rejection_reason)}</div>
                    </div>
                ` : ''}

                ${c.notes ? `
                    <div class="client-info-block" style="grid-column: 1 / -1;">
                        <h4>${_t('clients.card.block.notes', 'Заметки')}</h4>
                        <div style="white-space: pre-wrap;">${escapeHtml(c.notes)}</div>
                    </div>
                ` : ''}

                ${tags.length ? `
                    <div class="client-info-block" style="grid-column: 1 / -1;">
                        <h4>${_t('clients.card.block.tags', 'Теги')}</h4>
                        ${tags.map(t => `<span style="background:#dbeafe;color:#1e40af;padding:2px 10px;border-radius:12px;margin-right:6px;">${escapeHtml(t)}</span>`).join('')}
                    </div>
                ` : ''}
            </div>

            <div style="margin-top: 24px; display: flex; align-items: center; gap: 12px;">
                <h3 style="margin: 0; flex: 1; font-size: 16px;">${_t('clients.card.estimates_title', 'Сметы клиента')}</h3>
                <button id="client-new-estimate-btn" class="btn-primary">${_t('clients.card.new_estimate', '+ Новая смета')}</button>
            </div>
            ${renderClientPipelineSummary(STATE.currentEstimates || [])}
            <div style="margin-top: 12px;">${estimatesHtml}</div>
        </div>
    `;
}

// Sprint 19e: суммарный pipeline всех смет клиента — Quick overview "что где"
function renderClientPipelineSummary(estimates) {
    const active = estimates.filter(e => !e.is_archived_variant && e.status !== 'cancelled');
    if (active.length === 0) return '';

    const buckets = {
        sales:   active.filter(e => ['draft','sent','confirmed'].includes(e.status)),
        finance: active.filter(e => e.status === 'prepaid'),
        ops:     active.filter(e => ['in_progress','paid'].includes(e.status)),
        done:    active.filter(e => ['completed','closed'].includes(e.status))
    };
    const totalActive = buckets.sales.length + buckets.finance.length + buckets.ops.length;
    const cancelled = estimates.filter(e => e.status === 'cancelled').length;

    const card = (label, items, color, icon) => {
        if (items.length === 0) return '';
        return `
            <div style="flex:1;min-width:140px;background:${color};border-radius:8px;padding:10px 12px;">
                <div style="font-size:11px;color: var(--text-secondary);display:flex;align-items:center;gap:6px;font-weight:600;text-transform:uppercase;letter-spacing:0.3px;">
                    <i data-lucide="${icon}" style="width:13px;height:13px"></i>
                    ${label}
                </div>
                <div style="font-size:20px;font-weight:700;color:#1e293b;margin-top:4px;">${items.length}</div>
                <div style="font-size:11px;color: var(--text-muted);margin-top:2px;">
                    ${items.slice(0,3).map(e => escapeHtml(e.tour_name || e.client_name || '?')).join(', ')}${items.length > 3 ? '…' : ''}
                </div>
            </div>
        `;
    };

    return `
        <div style="margin-top:12px;display:flex;gap:10px;flex-wrap:wrap;">
            ${card(_t('clients.summary.funnel', 'Воронка'), buckets.sales, '#dbeafe', 'send')}
            ${card(_t('clients.summary.prepayment', 'Предоплата'), buckets.finance, '#fce7f3', 'credit-card')}
            ${card(_t('clients.summary.in_progress', 'В работе'), buckets.ops, '#e0e7ff', 'plane')}
            ${card(_t('clients.summary.closed', 'Закрытые'), buckets.done, '#dcfce7', 'check-circle-2')}
            ${cancelled > 0 ? `<div style="background:#fee2e2;border-radius:8px;padding:10px 12px;min-width:100px;">
                <div style="font-size:11px;color:#991b1b;font-weight:600;text-transform:uppercase;">${_t('clients.summary.cancelled', 'Отменено')}</div>
                <div style="font-size:20px;font-weight:700;color:#991b1b;">${cancelled}</div>
            </div>` : ''}
        </div>
    `;
}

function rerender() {
    if (!_container) return;
    if (STATE.view === 'card') {
        _container.innerHTML = renderCardView();
    } else {
        _container.innerHTML = `${renderToolbar()}<div class="estimates-list-wrap">${renderListView()}</div>`;
    }
    bindEvents();
    // Sprint 18 UI/UX: re-init Lucide иконки после rerender
    if (window.lucide && typeof window.lucide.createIcons === 'function') {
        try { window.lucide.createIcons(); } catch (_) {}
    }
}

// ─── Events ─────────────────────────────────────────────────────────────────
function bindEvents() {
    if (!_container) return;

    if (STATE.view === 'list') {
        const search = _container.querySelector('#clients-search');
        if (search) {
            let timer;
            search.addEventListener('input', (e) => {
                clearTimeout(timer);
                timer = setTimeout(async () => {
                    STATE.search = e.target.value;
                    await loadList();
                    rerender();
                }, 250);
            });
        }
        const sort = _container.querySelector('#clients-sort');
        if (sort) sort.addEventListener('change', async (e) => {
            STATE.sort = e.target.value;
            await loadList();
            rerender();
        });
        // Sprint 18 Batch 3: assignment filter
        const assignedFilter = _container.querySelector('#clients-assigned-filter');
        if (assignedFilter) assignedFilter.addEventListener('change', async (e) => {
            STATE.assignedFilter = e.target.value;
            await loadList();
            rerender();
        });

        // Sprint 23 W6: source/status/contact/archived filters + tag cloud + duplicates
        const sourceFilter = _container.querySelector('#clients-source-filter');
        if (sourceFilter) sourceFilter.addEventListener('change', async (e) => {
            STATE.sourceFilter = e.target.value;
            await loadList();
            rerender();
        });
        const statusFilter = _container.querySelector('#clients-status-filter');
        if (statusFilter) statusFilter.addEventListener('change', async (e) => {
            STATE.statusFilter = e.target.value;
            await loadList();
            rerender();
        });
        const contactFilter = _container.querySelector('#clients-contact-filter');
        if (contactFilter) contactFilter.addEventListener('change', async (e) => {
            STATE.hasContactFilter = e.target.value;
            await loadList();
            rerender();
        });
        const archivedToggle = _container.querySelector('#clients-archived-toggle');
        if (archivedToggle) archivedToggle.addEventListener('change', async (e) => {
            STATE.archivedFilter = e.target.checked;
            await loadList();
            rerender();
        });
        const clearTags = _container.querySelector('#cl-clear-tags');
        if (clearTags) clearTags.addEventListener('click', async () => {
            STATE.tagsFilter = [];
            await loadList();
            rerender();
        });
        _container.querySelectorAll('[data-tag]').forEach(el => {
            el.addEventListener('click', async () => {
                const t = el.dataset.tag;
                if (STATE.tagsFilter.includes(t)) {
                    STATE.tagsFilter = STATE.tagsFilter.filter(x => x !== t);
                } else {
                    STATE.tagsFilter = [...STATE.tagsFilter, t];
                }
                await loadList();
                rerender();
            });
        });
        _container.querySelectorAll('[data-tag-remove]').forEach(el => {
            el.addEventListener('click', async () => {
                STATE.tagsFilter = STATE.tagsFilter.filter(x => x !== el.dataset.tagRemove);
                await loadList();
                rerender();
            });
        });
        const dupBtn = _container.querySelector('#clients-duplicates-btn');
        if (dupBtn) dupBtn.addEventListener('click', () => openDuplicatesModal());
        const dir = _container.querySelector('#clients-sort-dir');
        if (dir) dir.addEventListener('click', async () => {
            STATE.sortDir = STATE.sortDir === 'asc' ? 'desc' : 'asc';
            await loadList();
            rerender();
        });
        const newBtn = _container.querySelector('#clients-new-btn');
        if (newBtn) newBtn.addEventListener('click', openCreateModal);

        // Sprint 18: row-based interactions
        const table = _container.querySelector('.client-rows-table');
        if (table) {
            table.addEventListener('click', async (e) => {
                const row = e.target.closest('[data-client-id]');
                if (!row) return;
                const id = row.dataset.clientId;
                const actionEl = e.target.closest('[data-action]');
                const action = actionEl?.dataset?.action;
                const client = STATE.list.find(c => c.id === id);

                if (action === 'open-tour-card') {
                    e.stopPropagation();
                    const tourId = actionEl?.dataset?.tourId;
                    const eid = actionEl?.dataset?.estimateId;
                    if (tourId) {
                        location.hash = '#/tours/' + encodeURIComponent(tourId);
                    } else if (eid) {
                        // Sprint 22 W4: fallback на calculator если tour ещё не создан
                        // (data-fix migration 058 backfill-ит tour_id для existing estimates)
                        location.hash = '#/calculator?id=' + encodeURIComponent(eid);
                    }
                    return;
                }
                if (action === 'open-estimate' || action === 'open-estimate-btn') {
                    e.stopPropagation();
                    const tr = e.target.closest('[data-estimate-id]');
                    const eid = tr?.dataset?.estimateId || actionEl?.dataset?.estimateId;
                    if (!eid) return;
                    // Sprint 19c: row click → tour card; кнопка ↗ → прямой калькулятор
                    if (action === 'open-estimate-btn') {
                        location.hash = '#/calculator?id=' + encodeURIComponent(eid);
                    } else {
                        // Найти tour_id по estimate
                        const est = (STATE.inlineEstimatesCache[id] || []).find(x => x.id === eid);
                        if (est?.tour_id) {
                            location.hash = '#/tours/' + encodeURIComponent(est.tour_id);
                        } else {
                            // Fallback на калькулятор если tour_id не пришёл
                            location.hash = '#/calculator?id=' + encodeURIComponent(eid);
                        }
                    }
                    return;
                }
                if (action === 'delete-estimate') {
                    e.stopPropagation();
                    const eid = actionEl?.dataset?.estimateId;
                    if (!eid) return;
                    await deleteEstimateInline(eid, id);
                    return;
                }
                if (action === 'delete') {
                    e.stopPropagation();
                    if (!confirm(_t('clients.confirm.delete', 'Удалить клиента? Сметы остаются, но без привязки.'))) return;
                    try { await window.apiClient.deleteClient(id); await loadList(); rerender(); }
                    catch (err) { alert(_t('common.error', 'Ошибка') + ': ' + err.message); }
                    return;
                }
                if (action === 'open-card') {
                    e.stopPropagation();
                    STATE.view = 'card';
                    _container.innerHTML = `<div class="estimates-loading">${_t('common.loading', 'Загрузка...')}</div>`;
                    const res = await loadCard(id);
                    if (res.error) { _container.innerHTML = `<div class="estimates-error">${res.error}</div>`; return; }
                    // Sprint 19e: persist client id в hash чтобы возврат восстановил card view
                    history.replaceState(null, '', `#/clients?client=${encodeURIComponent(id)}`);
                    rerender();
                    return;
                }
                if (action === 'new-estimate' || action === 'new-estimate-link') {
                    e.stopPropagation();
                    if (client) prefillNewEstimateAndNavigate(client);
                    return;
                }
                if (action === 'toggle') {
                    // Toggle expand/collapse
                    if (STATE.expandedClientId === id) {
                        STATE.expandedClientId = null;
                        rerender();
                    } else {
                        STATE.expandedClientId = id;
                        // Render immediately (loading state) — затем re-render когда estimates загружены
                        rerender();
                        await loadInlineEstimates(id);
                        rerender();
                    }
                    return;
                }
            });
        }
    } else {
        // card view
        const back = _container.querySelector('#clients-back-btn');
        if (back) back.addEventListener('click', async () => {
            STATE.view = 'list';
            STATE.currentClient = null;
            STATE.currentEstimates = [];
            history.replaceState(null, '', '#/clients');
            await loadList();
            rerender();
        });
        const newEst = _container.querySelector('#client-new-estimate-btn');
        if (newEst) newEst.addEventListener('click', () => {
            // Открыть калькулятор как новую смету с предзаполненными данными клиента
            const c = STATE.currentClient;
            location.hash = '#/calculator?new=1';
            setTimeout(() => {
                if (window.QuoteCalc?.state) {
                    if (c.name) {
                        window.QuoteCalc.state.clientName = c.name;
                        const inp = document.getElementById('client-name'); if (inp) inp.value = c.name;
                    }
                    if (c.email) {
                        window.QuoteCalc.state.clientEmail = c.email;
                        const inp = document.getElementById('client-email'); if (inp) inp.value = c.email;
                    }
                    if (c.phone) {
                        window.QuoteCalc.state.clientPhone = c.phone;
                        const inp = document.getElementById('client-phone'); if (inp) inp.value = c.phone;
                    }
                    try { window.QuoteCalc.markQuoteAsUnsaved?.(); } catch (_) {}
                }
            }, 200);
        });
        // Sprint 18: click on shared estimates table — open / delete actions
        const estTable = _container.querySelector('.client-estimates-table');
        if (estTable) {
            estTable.addEventListener('click', async (e) => {
                const actionEl = e.target.closest('[data-action]');
                const action = actionEl?.dataset?.action;
                const tr = e.target.closest('[data-estimate-id]');
                const eid = actionEl?.dataset?.estimateId || tr?.dataset?.estimateId;

                // Sprint 19f: open-tour-card — отдельный action, не требует estimate_id
                if (action === 'open-tour-card') {
                    e.stopPropagation();
                    const tourId = actionEl?.dataset?.tourId;
                    const eid = actionEl?.dataset?.estimateId;
                    if (tourId) {
                        location.hash = '#/tours/' + encodeURIComponent(tourId);
                    } else if (eid) {
                        // Sprint 22 W4: fallback на calculator если tour ещё не создан
                        // (data-fix migration 058 backfill-ит tour_id для existing estimates)
                        location.hash = '#/calculator?id=' + encodeURIComponent(eid);
                    }
                    return;
                }

                if (!eid) return;

                if (action === 'delete-estimate') {
                    e.stopPropagation();
                    await deleteEstimateInline(eid, STATE.currentClient?.id);
                    return;
                }
                if (action === 'open-estimate-btn') {
                    e.stopPropagation();
                    location.hash = '#/calculator?id=' + encodeURIComponent(eid);
                    return;
                }
                // Default (row click or open-estimate) → tour card if available, else calculator
                const est = (STATE.currentEstimates || []).find(x => x.id === eid);
                if (est?.tour_id) {
                    location.hash = '#/tours/' + encodeURIComponent(est.tour_id);
                } else {
                    location.hash = '#/calculator?id=' + encodeURIComponent(eid);
                }
            });
        }
        const editBtn = _container.querySelector('#clients-edit-btn');
        if (editBtn) editBtn.addEventListener('click', openEditModal);
        // Sprint 18 Batch 3: reassign manager button
        const reassignBtn = _container.querySelector('#reassign-btn');
        if (reassignBtn) reassignBtn.addEventListener('click', openReassignModal);
    }
}

// Sprint 18 Batch 3: reassign modal
async function openReassignModal() {
    const c = STATE.currentClient;
    if (!c) return;

    const managers = await loadManagers();
    if (managers.length === 0) {
        alert(_t('clients.reassign.error_load', 'Не удалось загрузить список менеджеров'));
        return;
    }

    const currentManagerHtml = c.assigned_full_name
        ? escapeHtml(c.assigned_full_name)
        : `<em>${_t('clients.reassign.not_assigned', 'не назначен')}</em>`;

    const overlay = document.createElement('div');
    overlay.className = 'crm-modal-overlay';
    overlay.innerHTML = `
        <div class="crm-modal" style="max-width:480px;">
            <div class="crm-modal-header">
                <h3>${_t('clients.reassign.title', 'Переназначить клиента «{name}»').replace('{name}', escapeHtml(c.name))}</h3>
                <button class="crm-modal-close">&times;</button>
            </div>
            <form class="crm-modal-body" id="reassign-form" style="display:flex;flex-direction:column;gap:12px;">
                <div style="font-size:13px;color:var(--gray-600);">
                    ${_t('clients.reassign.current', 'Текущий менеджер: <strong>{name}</strong>').replace('{name}', currentManagerHtml)}
                </div>
                <label>${_t('clients.reassign.field.new_manager', 'Новый менеджер')}
                    <select name="assigned_to" required>
                        <option value="">${_t('clients.reassign.option.none', '— Без менеджера —')}</option>
                        ${managers.map(m => `<option value="${m.id}" ${m.id === c.assigned_to ? 'selected' : ''}>${escapeHtml(m.full_name || m.email)} (${escapeHtml(m.role)})</option>`).join('')}
                    </select>
                </label>
                <label>${_t('clients.reassign.field.notes', 'Заметка о передаче (опционально)')}
                    <textarea name="notes" rows="2" placeholder="${escapeHtml(_t('clients.reassign.notes_placeholder', 'Например: «Передаю Екатерине — у неё опыт по Аргентине»'))}"></textarea>
                </label>
                <div class="crm-modal-actions" style="display:flex;justify-content:flex-end;gap:8px;">
                    <button type="button" class="btn-ghost" data-action="cancel">${_t('btn.cancel', 'Отмена')}</button>
                    <button type="submit" class="btn-primary">${_t('clients.reassign.submit', 'Переназначить')}</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(overlay);

    overlay.querySelector('.crm-modal-close').addEventListener('click', () => overlay.remove());
    overlay.querySelector('[data-action="cancel"]').addEventListener('click', () => overlay.remove());
    overlay.querySelector('#reassign-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        const assigned_to = fd.get('assigned_to') || null;
        const notes = fd.get('notes') || null;
        try {
            const r = await fetchAuth('/api/v1/clients/' + encodeURIComponent(c.id) + '/assign', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ assigned_to, notes })
            });
            const d = await r.json();
            if (!d.success) throw new Error(d.error || 'reassign failed');
            overlay.remove();
            await loadCard(c.id);
            rerender();
        } catch (err) {
            alert(_t('clients.reassign.error_submit', 'Ошибка переназначения: {msg}').replace('{msg}', err.message));
        }
    });
}

// Sprint 18: shared delete-estimate logic for both inline + card view
async function deleteEstimateInline(estimateId, clientId) {
    if (!confirm(_t('clients.confirm.delete_estimate', 'Удалить эту смету? Действие можно отменить через админку.'))) return;
    try {
        const r = await fetchAuth('/api/v1/estimates/' + encodeURIComponent(estimateId), {
            method: 'DELETE'
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        // Invalidate cache + refresh
        if (clientId && STATE.inlineEstimatesCache[clientId]) {
            delete STATE.inlineEstimatesCache[clientId];
        }
        if (STATE.view === 'card' && clientId) {
            await loadCard(clientId);
            rerender();
        } else if (clientId) {
            await loadInlineEstimates(clientId);
            await loadList();
            rerender();
        } else {
            await loadList();
            rerender();
        }
    } catch (err) {
        alert(_t('clients.error.delete_estimate', 'Не удалось удалить смету: {msg}').replace('{msg}', err.message));
    }
}

// Sprint 18: prefill state.* + navigate to /calculator?new=1
function prefillNewEstimateAndNavigate(client) {
    location.hash = '#/calculator?new=1';
    setTimeout(() => {
        if (window.QuoteCalc?.state) {
            const set = (k, v, inputId) => {
                if (v) {
                    window.QuoteCalc.state[k] = v;
                    if (inputId) {
                        const inp = document.getElementById(inputId);
                        if (inp) inp.value = v;
                    }
                }
            };
            set('clientId', client.id, 'client-id');
            set('clientName', client.name, 'client-name');
            set('clientEmail', client.email, 'client-email');
            set('clientPhone', client.phone, 'client-phone');
            try { window.QuoteCalc.markQuoteAsUnsaved?.(); } catch (_) {}
        }
    }, 200);
}

// ─── Modals (create / edit) ─────────────────────────────────────────────────
function openCreateModal() {
    openClientModal({});
}

function openEditModal() {
    if (STATE.currentClient) openClientModal(STATE.currentClient);
}

function openClientModal(client) {
    const isNew = !client.id;
    const status = client.status || 'lead';
    const source = client.source || 'unknown';
    const lang = client.preferred_language || 'ru';
    const followupISO = client.next_followup_at
        ? new Date(client.next_followup_at * 1000).toISOString().slice(0, 10)
        : '';
    const birthday = client.birthday || '';

    // Status options с эмодзи для визуального ranking
    const STATUSES = [
        ['lead', _t('clients.status.lead', '🌱 Лид')],
        ['negotiating', _t('clients.status.negotiating', '💬 Переговоры')],
        ['active', _t('clients.status.active', '✅ Активный')],
        ['completed', _t('clients.status.completed', '🎉 Завершён')],
        ['lost', _t('clients.status.lost', '❌ Потерян')],
        ['archived', _t('clients.status.archived', '📦 Архив')]
    ];
    const SOURCES = [
        ['unknown', _t('clients.source.unknown_full', '— Неизвестно')],
        ['instagram', _t('clients.source.instagram', '📷 Instagram')],
        ['google_ads', _t('clients.source.google_ads', '🔍 Google Ads')],
        ['referral', _t('clients.source.referral', '👥 Рекомендация')],
        ['agency', _t('clients.source.agency_full', '🏢 Агент-партнёр')],
        ['repeat', _t('clients.source.repeat_full', '🔁 Повторный клиент')],
        ['website', _t('clients.source.website', '🌐 Сайт')]
    ];
    const LANGS = [
        ['ru', _t('clients.modal.lang.ru', '🇷🇺 Русский')],
        ['en', _t('clients.modal.lang.en', '🇬🇧 English')],
        ['es', _t('clients.modal.lang.es', '🇪🇸 Español')]
    ];

    const overlay = document.createElement('div');
    overlay.className = 'crm-modal-overlay';
    overlay.innerHTML = `
        <div class="crm-modal" style="max-width:680px;">
            <div class="crm-modal-header">
                <h3>${isNew ? _t('clients.modal.title_new', 'Новый клиент') : _t('clients.modal.title_edit', 'Редактировать клиента')}</h3>
                <button class="crm-modal-close">&times;</button>
            </div>
            <form class="crm-modal-body" id="client-form" style="display:grid;grid-template-columns:1fr 1fr;gap:14px;">
                <!-- Базовые контакты — full width -->
                <label style="grid-column:1/-1;">${_t('clients.modal.field.name', 'Имя')} <span style="color:#dc2626;">*</span>
                    <input name="name" required value="${escapeHtml(client.name || '')}" />
                </label>
                <label>${_t('clients.modal.field.email', 'Email')}
                    <input name="email" type="email" value="${escapeHtml(client.email || '')}" />
                </label>
                <label>${_t('clients.modal.field.phone', 'Телефон')}
                    <input name="phone" value="${escapeHtml(client.phone || '')}" />
                </label>
                <label>${_t('clients.modal.field.telegram', 'Telegram (без @)')}
                    <input name="telegram" value="${escapeHtml(client.telegram || '')}" />
                </label>
                <label>${_t('clients.modal.field.company', 'Компания')}
                    <input name="company" value="${escapeHtml(client.company || '')}" />
                </label>
                <label style="display:flex;align-items:center;gap:6px;">
                    <input name="whatsapp" type="checkbox" ${client.whatsapp ? 'checked' : ''} />
                    ${_t('clients.modal.field.whatsapp', 'На номере WhatsApp')}
                </label>
                <label style="display:flex;align-items:center;gap:6px;">
                    <input name="is_vip" type="checkbox" ${client.is_vip ? 'checked' : ''} />
                    ${_t('clients.modal.field.is_vip', '⭐ VIP клиент')}
                </label>

                <!-- Sprint 18: business fields -->
                <label>${_t('clients.modal.field.status', 'Статус')}
                    <select name="status">
                        ${STATUSES.map(([v, label]) => `<option value="${v}" ${v === status ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                </label>
                <label>${_t('clients.modal.field.source', 'Источник')}
                    <select name="source">
                        ${SOURCES.map(([v, label]) => `<option value="${v}" ${v === source ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                </label>
                <label>${_t('clients.modal.field.next_followup', 'Следующий контакт')}
                    <input name="next_followup_date" type="date" value="${followupISO}" />
                </label>
                <label>${_t('clients.modal.field.birthday', 'День рождения')}
                    <input name="birthday" type="date" value="${birthday}" />
                </label>
                <label>${_t('clients.modal.field.country', 'Страна')}
                    <input name="country" value="${escapeHtml(client.country || '')}" />
                </label>
                <label>${_t('clients.modal.field.city', 'Город')}
                    <input name="city" value="${escapeHtml(client.city || '')}" />
                </label>
                <label>${_t('clients.modal.field.preferred_language', 'Язык общения')}
                    <select name="preferred_language">
                        ${LANGS.map(([v, label]) => `<option value="${v}" ${v === lang ? 'selected' : ''}>${label}</option>`).join('')}
                    </select>
                </label>
                <label style="display:flex;align-items:center;gap:6px;">
                    <input name="gdpr_consent" type="checkbox" ${client.gdpr_consent_at ? 'checked' : ''} />
                    ${_t('clients.modal.field.gdpr_consent', 'Согласие на обработку данных (GDPR)')}
                </label>

                <label style="grid-column:1/-1;">${_t('clients.modal.field.notes', 'Заметки')}
                    <textarea name="notes" rows="3">${escapeHtml(client.notes || '')}</textarea>
                </label>
                <label style="grid-column:1/-1;${status === 'lost' ? '' : 'display:none;'}" data-field="rejection-reason">
                    ${_t('clients.modal.field.rejection_reason', 'Причина отказа (если потерян)')}
                    <textarea name="rejection_reason" rows="2">${escapeHtml(client.rejection_reason || '')}</textarea>
                </label>

                <div class="crm-modal-actions" style="grid-column:1/-1;display:flex;justify-content:flex-end;gap:8px;">
                    <button type="button" class="btn-ghost" data-action="cancel">${_t('btn.cancel', 'Отмена')}</button>
                    <button type="submit" class="btn-primary">${isNew ? _t('btn.create', 'Создать') : _t('btn.save', 'Сохранить')}</button>
                </div>
            </form>
        </div>
    `;
    document.body.appendChild(overlay);

    // Show/hide rejection reason based on status select
    const statusSel = overlay.querySelector('select[name="status"]');
    const rejectionLabel = overlay.querySelector('[data-field="rejection-reason"]');
    if (statusSel && rejectionLabel) {
        statusSel.addEventListener('change', () => {
            rejectionLabel.style.display = statusSel.value === 'lost' ? 'block' : 'none';
        });
    }

    overlay.querySelector('.crm-modal-close').addEventListener('click', () => overlay.remove());
    overlay.querySelector('[data-action="cancel"]').addEventListener('click', () => overlay.remove());
    overlay.querySelector('#client-form').addEventListener('submit', async (e) => {
        e.preventDefault();
        const fd = new FormData(e.target);
        const followupDate = fd.get('next_followup_date');
        const followupTs = followupDate ? Math.floor(new Date(followupDate + 'T12:00:00').getTime() / 1000) : null;
        const gdprCheck = fd.get('gdpr_consent') === 'on';
        const data = {
            name: fd.get('name'),
            email: fd.get('email'),
            phone: fd.get('phone'),
            telegram: fd.get('telegram'),
            company: fd.get('company'),
            notes: fd.get('notes'),
            whatsapp: fd.get('whatsapp') === 'on',
            // Sprint 18 business fields
            status: fd.get('status'),
            source: fd.get('source'),
            next_followup_at: followupTs,
            birthday: fd.get('birthday') || null,
            country: fd.get('country') || null,
            city: fd.get('city') || null,
            preferred_language: fd.get('preferred_language'),
            is_vip: fd.get('is_vip') === 'on',
            gdpr_consent_at: gdprCheck ? (client.gdpr_consent_at || Math.floor(Date.now() / 1000)) : null,
            rejection_reason: fd.get('rejection_reason') || null
        };
        try {
            if (isNew) {
                await window.apiClient.createClient(data);
            } else {
                await window.apiClient.updateClient(client.id, data);
            }
            overlay.remove();
            if (STATE.view === 'card' && client.id) {
                await loadCard(client.id);
            } else {
                await loadList();
            }
            rerender();
        } catch (err) {
            alert(_t('common.error', 'Ошибка') + ': ' + err.message);
        }
    });
}

// i18n: re-render section when locale changes; cleanup on unmount.
let _i18nListener = null;
function setupI18nListener() {
    if (_i18nListener) return;
    _i18nListener = () => {
        // Re-render текущего DOM section с новыми переводами.
        if (_container) rerender();
    };
    document.addEventListener('i18n:locale-changed', _i18nListener);
    window.addEventListener('crm:section-will-unmount', () => {
        if (_i18nListener) {
            document.removeEventListener('i18n:locale-changed', _i18nListener);
            _i18nListener = null;
        }
    }, { once: true });
}

// ─── Entry ──────────────────────────────────────────────────────────────────
// Sprint 23 W6: duplicates discovery + merge UI
async function openDuplicatesModal() {
    const overlay = document.createElement('div');
    overlay.className = 'crm-modal-overlay';
    overlay.innerHTML = `
        <div class="crm-modal" style="max-width:820px;max-height:90vh;overflow:auto;">
            <div class="crm-modal-header">
                <h3>🔍 Возможные дубликаты клиентов</h3>
                <button class="crm-modal-close">&times;</button>
            </div>
            <div class="crm-modal-body" id="dup-body">
                <div style="padding:30px;text-align:center;color:var(--text-muted);">Поиск дубликатов…</div>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);
    overlay.querySelector('.crm-modal-close').addEventListener('click', () => overlay.remove());
    overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

    try {
        const r = await fetchAuth('/api/v1/clients/duplicates');
        if (!r.ok) throw new Error('HTTP ' + r.status);
        const groups = (await r.json()).data || [];
        const body = overlay.querySelector('#dup-body');

        if (groups.length === 0) {
            body.innerHTML = `<div style="padding:40px;text-align:center;color:var(--text-muted);">
                Дубликатов не найдено 🎉<br><small>Сравнение идёт по email и нормализованному телефону.</small>
            </div>`;
            return;
        }

        body.innerHTML = groups.map((g, gi) => `
            <div class="dup-group" style="margin-bottom:20px;padding:14px;background:#f9fafb;border-radius:8px;">
                <div style="font-size:12px;color:var(--text-muted);margin-bottom:8px;">
                    Совпадение по <strong>${g.type === 'email' ? 'email' : 'телефону'}</strong>:
                    <code style="background:#fff;padding:2px 6px;border-radius:3px;">${escapeHtml(g.key)}</code>
                </div>
                <table style="width:100%;border-collapse:collapse;font-size:13px;background:#fff;border-radius:6px;overflow:hidden;">
                    <thead><tr style="background:#f1f5f9;">
                        <th style="padding:8px;text-align:left;width:70px;">Главный</th>
                        <th style="padding:8px;text-align:left;">Имя</th>
                        <th style="padding:8px;text-align:left;">Контакты</th>
                        <th style="padding:8px;text-align:left;">Источник</th>
                        <th style="padding:8px;text-align:right;">Смет / Сумма</th>
                    </tr></thead>
                    <tbody>
                        ${g.clients.map((c, ci) => {
                            const sm = SOURCE_META[c.source] || SOURCE_META.unknown;
                            return `<tr>
                                <td style="padding:8px;"><input type="radio" name="primary-${gi}" value="${escapeHtml(c.id)}" ${ci === 0 ? 'checked' : ''} /></td>
                                <td style="padding:8px;"><strong>${escapeHtml(c.name || '(без имени)')}</strong></td>
                                <td style="padding:8px;font-size:11px;color:var(--text-muted);">
                                    ${c.email ? escapeHtml(c.email) : '<span style="color:#dc2626;">нет email</span>'}<br>
                                    ${c.phone ? escapeHtml(c.phone) : '<span style="color:#dc2626;">нет phone</span>'}
                                </td>
                                <td style="padding:8px;"><span style="display:inline-flex;gap:3px;padding:2px 7px;border-radius:8px;color:#fff;background:${sm.color};font-size:10px;">${sm.icon} ${sm.label}</span></td>
                                <td style="padding:8px;text-align:right;">${c.estimates_count || 0} / ${fmtCurrency(c.total_estimated)}</td>
                            </tr>`;
                        }).join('')}
                    </tbody>
                </table>
                <button class="btn-primary" data-merge-group="${gi}" style="margin-top:10px;font-size:13px;">
                    Объединить эту группу в выбранного главного
                </button>
            </div>
        `).join('');

        body.querySelectorAll('[data-merge-group]').forEach(btn => {
            btn.addEventListener('click', async () => {
                const gi = parseInt(btn.dataset.mergeGroup, 10);
                const sel = body.querySelector(`input[name="primary-${gi}"]:checked`);
                if (!sel) { alert('Выберите главного клиента (radio)'); return; }
                const primaryId = sel.value;
                const mergeIds = groups[gi].clients.map(c => c.id).filter(id => id !== primaryId);
                const primaryName = groups[gi].clients.find(c => c.id === primaryId)?.name || '?';
                if (!confirm(`Объединить ${mergeIds.length} клиентов в "${primaryName}"?\n\nСметы перейдут к главному, остальные карточки будут удалены (мягко).`)) return;
                btn.disabled = true; btn.textContent = 'Объединение…';
                try {
                    const r = await fetchAuth(`/api/v1/clients/${primaryId}/merge`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ merge_ids: mergeIds })
                    });
                    if (!r.ok) throw new Error('HTTP ' + r.status);
                    const result = await r.json();
                    btn.parentElement.style.opacity = '0.4';
                    btn.textContent = `✓ Объединено (${result.data.mergedCount} клиентов, ${result.data.reassignCount} смет)`;
                    // Reload main list
                    await loadList();
                    await loadTagCloud();
                    await loadSourceCounts();
                    rerender();
                } catch (err) {
                    alert('Ошибка слияния: ' + err.message);
                    btn.disabled = false; btn.textContent = 'Объединить эту группу в выбранного главного';
                }
            });
        });
    } catch (err) {
        overlay.querySelector('#dup-body').innerHTML = `<div style="padding:30px;color:#dc2626;">Ошибка: ${escapeHtml(err.message)}</div>`;
    }
}

export async function renderClientsSection() {
    const root = document.createElement('div');
    root.className = 'estimates-section';
    root.innerHTML = `<div class="estimates-loading">${_t('common.loading', 'Загрузка...')}</div>`;
    _container = root;

    // Sprint 19e: восстанавливаем card view если есть ?client=ID в hash.
    // Это сохраняет navigation context (back из калькулятора → карточка клиента).
    const hashMatch = location.hash.match(/[?&]client=([^&]+)/);
    const persistedClientId = hashMatch ? decodeURIComponent(hashMatch[1]) : null;

    // Sprint 23 W6: parallel-load list + tag cloud + source counts
    Promise.all([loadList(), loadTagCloud(), loadSourceCounts()]).then(async ([res]) => {
        if (res.error) { root.innerHTML = `<div class="estimates-error">${_t('common.error', 'Ошибка')}: ${res.error}</div>`; return; }

        if (persistedClientId) {
            const cardRes = await loadCard(persistedClientId);
            if (cardRes.ok) {
                STATE.view = 'card';
                rerender();
                setupI18nListener();
                return;
            }
        }
        STATE.view = 'list';
        rerender();
        setupI18nListener();
    });

    return root;
}
